import type { WorkAuthorizationExpirationRow } from 'api/admin/i9/getWorkAuthorizationExpirationReport'
import { addDays, format, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { EXPIRATION_OPTIONS } from '../components/I9HubFilters/ExpirationWindowUrlFilter'

export type AltDocHubRow = {
  workerId: number
  workerName: string
  workerMarket: string
  workerW2States: string[]
  /** yyyy-MM-dd or ISO string; used for grouping / sorting */
  documentDate: string
}

export type AltDocsGroupBy = 'state' | 'market' | 'date'
export type AltDocsDateSort = 'asc' | 'desc'

export type ReverificationGroupBy = 'state' | 'market' | 'expiration_bucket'

export type ReverificationExpirationFilterDays =
  (typeof EXPIRATION_OPTIONS)[number]

export function toYmd(value: string) {
  const date = parseISO(value)
  return value.includes('T')
    ? formatInTimeZone(date, 'UTC', 'yyyy-MM-dd')
    : format(date, 'yyyy-MM-dd')
}

export function workAuthExpirationYmd(row: WorkAuthorizationExpirationRow) {
  return toYmd(row.i9WorkAuthorizationExpirationDate)
}

function documentExpirationYmd(value: string | null) {
  if (!value) return null
  return toYmd(value)
}

export type WorkAuthorizationExpiringDocument = {
  documentType: string | null
  expirationDate: string | null
}

/** Document whose expiration matches work authorization (List A, B, C, or Supplement B). */
export function getWorkAuthorizationExpiringDocument(
  row: WorkAuthorizationExpirationRow
): WorkAuthorizationExpiringDocument {
  const workAuthYmd = workAuthExpirationYmd(row)

  const candidates: WorkAuthorizationExpiringDocument[] = [
    {
      documentType: row.listADocumentType,
      expirationDate: row.listADocumentExpiration,
    },
    {
      documentType: row.listBDocumentType,
      expirationDate: row.listBDocumentExpiration,
    },
    {
      documentType: row.listCDocumentType,
      expirationDate: row.listCDocumentExpiration,
    },
    {
      documentType: row.suppBDocumentType,
      expirationDate: row.suppBDocumentExpiration,
    },
  ].filter((doc) => doc.expirationDate != null)

  const matchingWorkAuth = candidates.filter(
    (doc) => documentExpirationYmd(doc.expirationDate) === workAuthYmd
  )
  if (matchingWorkAuth.length > 0) {
    return matchingWorkAuth[0]
  }

  const sortedByExpiration = [...candidates].sort((firstDoc, secondDoc) =>
    documentExpirationYmd(firstDoc.expirationDate)!.localeCompare(
      documentExpirationYmd(secondDoc.expirationDate)!
    )
  )
  if (sortedByExpiration.length > 0) {
    return sortedByExpiration[0]
  }

  return { documentType: null, expirationDate: null }
}

export function rowWithinExpirationWindow(
  row: WorkAuthorizationExpirationRow,
  windowDays: number
) {
  const expYmd = workAuthExpirationYmd(row)
  const limitYmd = format(addDays(new Date(), windowDays), 'yyyy-MM-dd')
  return expYmd <= limitYmd
}

export function primaryW2State(row: { workerW2States: string[] }) {
  const states = [...row.workerW2States].sort((stateA, stateB) =>
    stateA.localeCompare(stateB)
  )
  return states[0] ?? 'Unknown State'
}

export function filterReverificationRows(
  rows: WorkAuthorizationExpirationRow[],
  filters: {
    expirationDays: ReverificationExpirationFilterDays
    selectedMarket: string | null
    selectedState: string | null
    workerId: number | null
    workerSearch: string
  }
) {
  const searchQuery = filters.workerSearch.trim().toLowerCase()
  return rows.filter((row) => {
    if (!rowWithinExpirationWindow(row, filters.expirationDays)) return false
    if (filters.workerId != null && row.workerId !== filters.workerId)
      return false
    if (filters.selectedMarket && row.workerMarket !== filters.selectedMarket)
      return false
    if (
      filters.selectedState &&
      !row.workerW2States.includes(filters.selectedState)
    ) {
      return false
    }
    if (searchQuery && !row.workerName.toLowerCase().includes(searchQuery))
      return false
    return true
  })
}

export function sortByWorkAuthExpirationAsc(
  rows: WorkAuthorizationExpirationRow[]
) {
  return [...rows].sort((firstRow, secondRow) =>
    workAuthExpirationYmd(firstRow).localeCompare(
      workAuthExpirationYmd(secondRow)
    )
  )
}

function reverificationBucketLabel(row: WorkAuthorizationExpirationRow) {
  const exp = workAuthExpirationYmd(row)
  const d30 = format(addDays(new Date(), 30), 'yyyy-MM-dd')
  const d60 = format(addDays(new Date(), 60), 'yyyy-MM-dd')
  const d90 = format(addDays(new Date(), 90), 'yyyy-MM-dd')
  if (exp <= d30) return 'Within 30 days'
  if (exp <= d60) return '31–60 days'
  if (exp <= d90) return '61–90 days'
  return 'Beyond 90 days'
}

export function groupReverificationRows(
  rows: WorkAuthorizationExpirationRow[],
  groupBy: ReverificationGroupBy
): [string, WorkAuthorizationExpirationRow[]][] {
  if (groupBy === 'expiration_bucket') {
    const order = [
      'Within 30 days',
      '31–60 days',
      '61–90 days',
      'Beyond 90 days',
    ]
    const grouped = rows.reduce(
      (acc, row) => {
        const label = reverificationBucketLabel(row)
        if (!acc[label]) acc[label] = []
        acc[label].push(row)
        return acc
      },
      {} as Record<string, WorkAuthorizationExpirationRow[]>
    )
    return order
      .filter((label) => grouped[label]?.length)
      .map(
        (label) => [label, sortByWorkAuthExpirationAsc(grouped[label])] as const
      )
  }

  const keyFn =
    groupBy === 'state'
      ? (row: WorkAuthorizationExpirationRow) => primaryW2State(row)
      : (row: WorkAuthorizationExpirationRow) => row.workerMarket || 'Unknown'

  const grouped = rows.reduce(
    (acc, row) => {
      const groupKey = keyFn(row)
      if (!acc[groupKey]) acc[groupKey] = []
      acc[groupKey].push(row)
      return acc
    },
    {} as Record<string, WorkAuthorizationExpirationRow[]>
  )

  return Object.entries(grouped)
    .sort(([labelA], [labelB]) => labelA.localeCompare(labelB))
    .map(
      ([label, groupRows]) =>
        [label, sortByWorkAuthExpirationAsc(groupRows)] as const
    )
}

function documentDateYmd(row: AltDocHubRow) {
  return row.documentDate.slice(0, 10)
}

export function filterAltDocRows(
  rows: AltDocHubRow[],
  filters: {
    selectedMarket: string | null
    selectedState: string | null
    workerId: number | null
    workerSearch: string
  }
) {
  const searchQuery = filters.workerSearch.trim().toLowerCase()
  return rows.filter((row) => {
    if (filters.workerId != null && row.workerId !== filters.workerId)
      return false
    if (filters.selectedMarket && row.workerMarket !== filters.selectedMarket)
      return false
    if (
      filters.selectedState &&
      !row.workerW2States.includes(filters.selectedState)
    ) {
      return false
    }
    if (searchQuery && !row.workerName.toLowerCase().includes(searchQuery))
      return false
    return true
  })
}

export function groupAltDocRows(
  rows: AltDocHubRow[],
  groupBy: AltDocsGroupBy,
  dateSort: AltDocsDateSort
): [string, AltDocHubRow[]][] {
  const sortWithinGroup = (groupRows: AltDocHubRow[]) => {
    const sorted = [...groupRows].sort((firstRow, secondRow) =>
      documentDateYmd(firstRow).localeCompare(documentDateYmd(secondRow))
    )
    return dateSort === 'desc' ? sorted.reverse() : sorted
  }

  if (groupBy === 'date') {
    const grouped = rows.reduce(
      (acc, row) => {
        const dateKey = documentDateYmd(row)
        if (!acc[dateKey]) acc[dateKey] = []
        acc[dateKey].push(row)
        return acc
      },
      {} as Record<string, AltDocHubRow[]>
    )
    const entries = Object.entries(grouped)
    entries.sort(([dateA], [dateB]) =>
      dateSort === 'asc'
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA)
    )
    return entries.map(
      ([dateKey, groupRows]) => [dateKey, sortWithinGroup(groupRows)] as const
    )
  }

  const keyFn =
    groupBy === 'state'
      ? (row: AltDocHubRow) => primaryW2State(row)
      : (row: AltDocHubRow) => row.workerMarket || 'Unknown'

  const grouped = rows.reduce(
    (acc, row) => {
      const groupKey = keyFn(row)
      if (!acc[groupKey]) acc[groupKey] = []
      acc[groupKey].push(row)
      return acc
    },
    {} as Record<string, AltDocHubRow[]>
  )

  return Object.entries(grouped)
    .sort(([labelA], [labelB]) => labelA.localeCompare(labelB))
    .map(([label, groupRows]) => [label, sortWithinGroup(groupRows)] as const)
}
